"use client";

import { motion, useMotionValue, useSpring } from "motion/react";

export type FluidGradientTextProps = {
  /** Text content rendered inside the SVG. */
  text: string;
  /**
   * SVG viewBox width used to scale the gradient and text layout.
   * @default 1200
   * */
  svgViewBoxWidth?: number;
  /**
   * SVG viewBox height used as the base text size.
   * @default 300
   * */
  svgViewBoxHeight?: number;
};

export function FluidGradientText({
  text,
  svgViewBoxWidth = 1200,
  svgViewBoxHeight = 300,
}: FluidGradientTextProps) {
  const gradientX1Raw = useMotionValue(svgViewBoxWidth / 2);
  const gradientX1 = useSpring(gradientX1Raw, {
    damping: 30,
    mass: 0.5,
    stiffness: 200,
  });

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const containerRect = container.getBoundingClientRect();
    const mouseX = event.clientX - containerRect.left;
    const containerWidth = containerRect.width;

    const normalizedX = (mouseX / containerWidth) * svgViewBoxWidth;
    const clampedX = Math.max(0, Math.min(svgViewBoxWidth, normalizedX));

    gradientX1Raw.set(clampedX);
  };

  const handleMouseLeave = () => {
    gradientX1Raw.set(svgViewBoxWidth / 2);
  };

  return (
    <div
      className="relative size-auto overflow-hidden after:absolute after:bottom-0 after:h-px after:w-full after:bg-current/15"
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <svg
        className="size-full translate-y-[37.5%] select-none"
        fill="none"
        viewBox={`0 0 ${svgViewBoxWidth} ${svgViewBoxHeight}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          dominantBaseline="central"
          fill="url(#fluid_gradient_text_linear)"
          stroke="currentColor"
          strokeOpacity="0.1"
          strokeWidth="2"
          style={{
            fontFamily: "Helvetica",
            fontSize: svgViewBoxHeight,
            fontWeight: "bold",
          }}
          textAnchor="middle"
          x="50%"
          y="50%"
        >
          {text}
        </text>
        <defs>
          <motion.linearGradient
            gradientUnits="userSpaceOnUse"
            id="fluid_gradient_text_linear"
            x1={gradientX1}
            x2={svgViewBoxWidth / 2}
            y1="0"
            y2={svgViewBoxHeight}
          >
            <stop offset="0.625" stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </motion.linearGradient>
        </defs>
      </svg>
    </div>
  );
}
